// BUILD=R32-selection-images-expand-stability
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Fuse from "fuse.js";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { Loader2, Printer, FileCheck2, FileSpreadsheet, Download } from "lucide-react";
import { generateSikkensCenterExcel, SIKKENS_CENTER_SUPPLIER } from "@/lib/exportSikkensCenter";
import { useSikkensExportLog, formatExportedAt } from "@/hooks/useSikkensExportLog";
import type { BadgeItemMeta } from "./BadgeRow";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/context/PermissionsContext";
import {
  useOrdersDecoprof,
  useOrdersDecoprofAfgerond,
  useOrdersDecoprofFlash,
  useUpdateOrdersDecoprofStatus,
  type OrdersDecoprofRow,
} from "@/hooks/useOrdersDecoprof";
import {
  ORDER_DECOPROF_TYPE_OPTIONS,
  ORDER_DECOPROF_STATUSES,
  KNOWN_SUPPLIERS,
  printPackingSlipsBatch,
  getOrderTypes,
  getOrderSuppliers,
  isPickupShipment,
  isAllowedPickupCustomer,
  type OrderDecoprofType,
  type OrderDecoprofStatus,
  isMobileDevice,
} from "@/lib/ordersDecoprof";

import BadgeStatus3D from "./BadgeStatus3D";
import OrderStatusDropdown from "./OrderStatusDropdown";
import BadgeRow from "./BadgeRow";
import Card3D from "./Card3D";
import OrderLinesExpansion from "./OrderLinesExpansion";
import Button3D from "./Button3D";
import Tile3DIconButton from "./Tile3DIconButton";
import KlantInlichtenButton from "@/components/shared/KlantInlichtenButton";
import Filter3DDropdown from "./Filter3DDropdown";
import ScrollHelpers from "./ScrollHelpers";
import ExportConfirmDialog from "./ExportConfirmDialog";
import KlantHistorySheet from "./KlantHistorySheet";

import { usePrefetchOnVisible } from "@/hooks/usePrefetchOnVisible";
import { useOrderIdsWithNotes } from "@/hooks/useOrderLineNotes";
import { StickyNote, Mail, Phone, ArrowRight, History } from "lucide-react";

// R45: kleine sub-component zodat we per kaart een ref kunnen koppelen aan de
// IntersectionObserver-prefetch zonder Card3D te hoeven wijzigen.
const PrefetchSentinel = ({ orderid }: { orderid: number | null }) => {
  const ref = usePrefetchOnVisible(orderid);
  return <div ref={ref} aria-hidden className="h-0 w-0" />;
};

// R72: NL-formaat "5 mei 2026, 14:32" voor orderdatum+tijd in overzichtsregel.
const ORDER_DATE_FMT = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric", month: "long", year: "numeric",
  hour: "2-digit", minute: "2-digit",
});
const formatOrderDateTime = (iso: string): string => {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  // Intl voegt " om " in tussen datum en tijd; vervang door ", " voor compacte weergave
  return ORDER_DATE_FMT.format(new Date(t)).replace(" om ", ", ");
};

const STATUS_PILL_COLOR: Record<string, string> = {
  Nieuw: "bg-blue-500 text-white ring-blue-300/50",
  Uitgeprint: "bg-emerald-500 text-white ring-emerald-300/50",
  Aangemaakt: "bg-violet-500 text-white ring-violet-300/50",
  "Nalevering winkel": "bg-sky-600 text-white ring-sky-300/50",
  "Behangorder besteld": "bg-fuchsia-600 text-white ring-fuchsia-300/50",
  "Afwijkende verzenddatum": "bg-rose-600 text-white ring-rose-300/50",
  "In afwachting van klant": "bg-yellow-600 text-white ring-yellow-300/50",
  "Afgerond": "bg-slate-500 text-white ring-slate-300/50",
};
const TYPE_PILL_COLOR: Record<string, string> = {
  Verfbestelling: "bg-orange-500 text-white ring-orange-300/50",
  Behangorder: "bg-teal-500 text-white ring-teal-300/50",
  Spuitbusbestelling: "bg-amber-500 text-white ring-amber-300/50",
  Overige: "bg-pink-500 text-white ring-pink-300/50",
};

const formatShippingDate = (d: string | null): string => {
  if (!d) return "";
  // shippingdate is een date (YYYY-MM-DD)
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const formatCreatedAtAsDate = (iso: string | null): string => {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const d = new Date(t);
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const parseShippingDate = (value: string | null): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  parsed.setHours(0, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isFutureShippingDate = (value: string | null): boolean => {
  const shippingDate = parseShippingDate(value);
  if (!shippingDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return shippingDate.getTime() > today.getTime();
};

// Eén bron van waarheid voor klantnaam-rendering (desktop tabel + mobile card).
// Volgorde: voornaam_klant + achternaam_klant → billing_address (first/last/voornaam/achternaam)
// → company → straat + nummer → "Onbekende klant".
const getKlantnaam = (order: OrdersDecoprofRow): string => {
  const directName = [order.voornaam_klant, order.achternaam_klant].filter(Boolean).join(" ").trim();
  if (directName) return directName;

  const billing = order.billing_address as Record<string, unknown> | null;
  if (billing && typeof billing === "object") {
    const get = (...keys: string[]) => {
      for (const key of keys) {
        const value = billing[key];
        if (typeof value === "string" && value.trim()) return value.trim();
      }
      return "";
    };

    const billingName = [
      get("firstname", "first_name", "voornaam"),
      get("lastname", "last_name", "achternaam"),
    ].filter(Boolean).join(" ").trim();

    if (billingName) return billingName;

    const companyName = get("company", "company_name");
    if (companyName) return companyName;

    const street = get("street", "straat");
    const number = get("number", "huisnummer", "house_number");
    const addressName = [street, number].filter(Boolean).join(" ").trim();
    if (addressName) return addressName;
  }

  // Fallback: email local-part (vóór @), netjes gehoofdletterd waar mogelijk.
  const email = order.emailadres_klant?.trim();
  if (email && email.includes("@")) {
    const local = email.split("@")[0];
    const cleaned = local.replace(/[._-]+/g, " ").trim();
    if (cleaned) {
      return cleaned
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }
  }

  // Laatste redmiddel vóór "Onbekende klant": klantnummer.
  if (order.customer_number) return `Klant #${order.customer_number}`;

  return "Onbekende klant";

};

// Backwards-compat alias (intern gebruik elders in dit bestand).
const extractCustomerDisplayName = getKlantnaam;

const getVisibleShippingDate = (order: Pick<OrdersDecoprofRow, "shippingdate" | "createdAt">) => {
  if (order.shippingdate) {
    return {
      label: formatShippingDate(order.shippingdate),
      isFallback: false,
      title: "Verzenddatum",
    };
  }

  const createdAtFallback = formatCreatedAtAsDate(order.createdAt);
  if (createdAtFallback) {
    return {
      label: createdAtFallback,
      isFallback: true,
      title: undefined,
    };
  }

  return null;
};


const OrdersDecoprofTab = () => {
  const { data: orders, isLoading, error } = useOrdersDecoprof();


  const { data: sikkensExportMap } = useSikkensExportLog();
  const { data: orderIdsWithNotes } = useOrderIdsWithNotes();
  const flashing = useOrdersDecoprofFlash();
  const updateStatus = useUpdateOrdersDecoprofStatus();
  const { hasPermission } = usePermissions();

  // ---------------------------------------------------------------------
  // URL-persistente filter/zoek state — bug Mark (via Jason): filters bleven
  // niet bewaard na navigatie. useSearchParams is single source of truth;
  // detail-Sheet en KlantHistorySheet leven binnen dezelfde route, dus
  // browser back/forward herstelt de URL (en dus filters) automatisch.
  // ---------------------------------------------------------------------
  const [searchParams, setSearchParams] = useSearchParams();

  const parseList = (key: string): string[] => {
    const raw = searchParams.get(key);
    if (!raw) return [];
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  };

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<OrderDecoprofType[]>(
    () => parseList("type") as OrderDecoprofType[],
  );
  const [statusFilter, setStatusFilter] = useState<OrderDecoprofStatus[]>(
    () => parseList("status") as OrderDecoprofStatus[],
  );
  const [supplierFilter, setSupplierFilter] = useState<string[]>(
    () => parseList("leverancier"),
  );
  // R71: validatie-filter — toont alleen orders met 2+ types (mixed orders).
  const [multiTypeOnly, setMultiTypeOnly] = useState<boolean>(
    () => searchParams.get("multiType") === "1",
  );
  // R75: validatie-filter — toont alleen orders met 2+ unieke leveranciers.
  const [multiSupplierOnly, setMultiSupplierOnly] = useState<boolean>(
    () => searchParams.get("multiSupplier") === "1",
  );
  // FASE 2 punt 1 — afhalen-in-winkel-toggle (default OFF: pickup-orders verborgen,
  // tenzij klant in ALLOW_CUSTOMER_NUMBERS allowlist).
  const [showPickup, setShowPickup] = useState<boolean>(
    () => searchParams.get("pickup") === "1",
  );
  // Zoekbalk voor afgeronde orders (alleen actief wanneer status-filter "Afgerond" bevat).
  const [afgerondSearch, setAfgerondSearch] = useState<string>(
    () => searchParams.get("q") ?? "",
  );

  // Sync state -> URL (replace, geen extra history entry).
  // We schrijven alleen wanneer er daadwerkelijk iets verandert om geen
  // navigation loops te triggeren.
  const syncSkipFirstRef = useRef(true);
  useEffect(() => {
    if (syncSkipFirstRef.current) {
      syncSkipFirstRef.current = false;
      return;
    }
    const next = new URLSearchParams(window.location.search);
    const setOrDel = (key: string, value: string) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    setOrDel("type", typeFilter.join(","));
    setOrDel("status", statusFilter.join(","));
    setOrDel("leverancier", supplierFilter.join(","));
    setOrDel("multiType", multiTypeOnly ? "1" : "");
    setOrDel("multiSupplier", multiSupplierOnly ? "1" : "");
    setOrDel("pickup", showPickup ? "1" : "");
    setOrDel("q", afgerondSearch.trim());
    const currentSearch = window.location.search.replace(/^\?/, "");
    const nextSearch = next.toString();
    if (currentSearch !== nextSearch) {
      setSearchParams(next, { replace: true });
    }
  }, [typeFilter, statusFilter, supplierFilter, multiTypeOnly, multiSupplierOnly, showPickup, afgerondSearch, setSearchParams]);


  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [printFailed, setPrintFailed] = useState<Set<string>>(new Set());
  const [printingOrders, setPrintingOrders] = useState<Set<string>>(new Set());
  const [printErrorReasons, setPrintErrorReasons] = useState<Record<string, string>>({});
  const lastClickedRef = useRef<string | null>(null);

  // Klant-historie sheet (klikbare klantnaam)
  const [historyTarget, setHistoryTarget] = useState<
    { name: string; number: string | null; email: string | null } | null
  >(null);


  const allSuppliers = useMemo(() => {
    const set = new Set<string>(KNOWN_SUPPLIERS);
    (orders ?? []).forEach((o) => o.suppliers.forEach((s) => set.add(s)));
    // R77: "Sikkens Center en Simonis" altijd bovenaan, rest alfabetisch (nl).
    return Array.from(set).sort((a, b) => {
      if (a === SIKKENS_CENTER_SUPPLIER && b !== SIKKENS_CENTER_SUPPLIER) return -1;
      if (b === SIKKENS_CENTER_SUPPLIER && a !== SIKKENS_CENTER_SUPPLIER) return 1;
      return a.localeCompare(b, "nl");
    });
  }, [orders]);

  // R71: aantal multi-type orders in de huidige geladen data (vóór filters),
  // gebruikt voor de teller in de toggle-knop.
  const multiTypeCount = useMemo(
    () => (orders ?? []).filter((o) => getOrderTypes(o.suppliers).length > 1).length,
    [orders],
  );
  // R75: aantal multi-leverancier orders in de huidige geladen data (vóór filters).
  const multiSupplierCount = useMemo(
    () => (orders ?? []).filter((o) => getOrderSuppliers(o.suppliers).length > 1).length,
    [orders],
  );

  // FASE 2 punt 1 — aantal pickup ('afhalen in winkel') orders in geladen data
  // dat NIET door de klant-allowlist (punt 2) toch wordt toegestaan.
  const pickupHiddenCount = useMemo(
    () =>
      (orders ?? []).filter(
        (o) => isPickupShipment(o.shipment_title) && !isAllowedPickupCustomer(o.customer_number),
      ).length,
    [orders],
  );

  const isAfgerondActief = statusFilter.includes("Afgerond");

  // Jason: Afgerond = Lightspeed status packed_completed / completed_shipped /
  // completed_picked_up (of expliciet orders_decoprof_status='Afgerond').
  // Lazy fetch — alleen wanneer Afgerond-filter actief is.
  const { data: afgerondOrders } = useOrdersDecoprofAfgerond(isAfgerondActief);

  // Bron-lijst: bij Afgerond-filter mergen we afgeronde orders erbij
  // (dedupe op ordernummer; afgerond-rij wint).
  const sourceOrders = useMemo(() => {
    if (!isAfgerondActief) return orders ?? [];
    const base = orders ?? [];
    const extra = afgerondOrders ?? [];
    if (extra.length === 0) return base;
    const map = new Map<string, OrdersDecoprofRow>();
    for (const o of base) map.set(o.ordernumber, o);
    for (const o of extra) map.set(o.ordernumber, o);
    return Array.from(map.values());
  }, [orders, afgerondOrders, isAfgerondActief]);

  const fuse = useMemo(() => {
    return new Fuse(sourceOrders, {
      keys: ["voornaam_klant", "achternaam_klant", "billing_address.firstname", "billing_address.lastname"],
      threshold: 0.4,
      ignoreLocation: true,
    });
  }, [sourceOrders]);



  const filteredOrders = useMemo(() => {
    let list = sourceOrders;
    // FASE 2 punt 1+2 — verberg pickup-orders behalve voor klanten in allowlist,
    // tenzij de gebruiker expliciet "Toon ook afhalen-orders" heeft aangezet.
    if (!showPickup) {
      list = list.filter(
        (o) => !isPickupShipment(o.shipment_title) || isAllowedPickupCustomer(o.customer_number),
      );
    }
    // R57 — FIX 4: type-filter matched op ELK type van de order (mixed orders).
    if (typeFilter.length > 0) {
      list = list.filter((o) =>
        getOrderTypes(o.suppliers).some((t) => typeFilter.includes(t)),
      );
    }
    if (statusFilter.length > 0) list = list.filter((o) => statusFilter.includes(o.status));
    if (supplierFilter.length > 0) {
      list = list.filter((o) => o.suppliers.some((s) => supplierFilter.includes(s)));
    }
    // R71: multi-type validatie-filter (combineerbaar met andere filters).
    if (multiTypeOnly) {
      list = list.filter((o) => getOrderTypes(o.suppliers).length > 1);
    }
    // R75: multi-leverancier validatie-filter (combineerbaar met andere filters).
    if (multiSupplierOnly) {
      list = list.filter((o) => getOrderSuppliers(o.suppliers).length > 1);
    }
    // Zoekbalk (zowel reguliere als Afgerond-weergave): ordernummer (starts-with),
    // klantnummer (exact), klantnaam (fuzzy via Fuse). Werkt op het al gefilterde
    // resultaat zodat het correct combineert met type/status/leverancier/afhalen.
    if (afgerondSearch.trim()) {
      const q = afgerondSearch.trim().toLowerCase();
      const qStripped = q.replace(/^vo\s*/i, "");
      const fuzzySet = new Set(fuse.search(afgerondSearch.trim()).map((r) => r.item.ordernumber));
      list = list.filter((o) => {
        const orderNum = (o.ordernumber ?? "").toLowerCase();
        if (orderNum === q || orderNum === qStripped) return true;
        if (orderNum.startsWith(q) || orderNum.startsWith(qStripped)) return true;
        if (o.customer_number && o.customer_number.toLowerCase() === q) return true;
        if (fuzzySet.has(o.ordernumber)) return true;
        return false;
      });
    }
    return list;
  }, [sourceOrders, typeFilter, statusFilter, supplierFilter, multiTypeOnly, multiSupplierOnly, showPickup, isAfgerondActief, afgerondSearch, fuse]);


  // Selection helpers
  const visibleOrdernumbers = useMemo(
    () => filteredOrders.map((o) => o.ordernumber),
    [filteredOrders],
  );
  const visibleSelectedCount = useMemo(
    () => visibleOrdernumbers.filter((n) => selected.has(n)).length,
    [visibleOrdernumbers, selected],
  );
  const hiddenSelectedCount = selected.size - visibleSelectedCount;

  // Selectie opschonen bij weergave-/filterwissel (NIET bij zoek-toetsaanslag):
  // alle ordernummers die niet meer in de zichtbare context passen worden uit
  // 'selected' verwijderd. We berekenen die context zonder afgerondSearch zodat
  // typen in de zoekbalk de selectie behoudt.
  const filterContextOrdernumbers = useMemo(() => {
    let list = sourceOrders;
    if (!showPickup) {
      list = list.filter(
        (o) => !isPickupShipment(o.shipment_title) || isAllowedPickupCustomer(o.customer_number),
      );
    }
    if (typeFilter.length > 0) {
      list = list.filter((o) =>
        getOrderTypes(o.suppliers).some((t) => typeFilter.includes(t)),
      );
    }
    if (statusFilter.length > 0) list = list.filter((o) => statusFilter.includes(o.status));
    if (supplierFilter.length > 0) {
      list = list.filter((o) => o.suppliers.some((s) => supplierFilter.includes(s)));
    }
    if (multiTypeOnly) list = list.filter((o) => getOrderTypes(o.suppliers).length > 1);
    if (multiSupplierOnly) list = list.filter((o) => getOrderSuppliers(o.suppliers).length > 1);
    return new Set(list.map((o) => o.ordernumber));
    // Bewust GEEN afgerondSearch in deps.
  }, [sourceOrders, showPickup, typeFilter, statusFilter, supplierFilter, multiTypeOnly, multiSupplierOnly]);

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set<string>();
      prev.forEach((n) => {
        if (filterContextOrdernumbers.has(n)) next.add(n);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [filterContextOrdernumbers]);

  const masterState: boolean | "indeterminate" =
    visibleOrdernumbers.length > 0 && visibleSelectedCount === visibleOrdernumbers.length
      ? true
      : visibleSelectedCount > 0
        ? "indeterminate"
        : false;

  const toggleMaster = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (visibleSelectedCount === visibleOrdernumbers.length) {
        // deselect all visible
        visibleOrdernumbers.forEach((n) => next.delete(n));
      } else {
        visibleOrdernumbers.forEach((n) => next.add(n));
      }
      return next;
    });
  };

  const toggleRow = (ordernumber: string, e?: React.MouseEvent | React.KeyboardEvent) => {
    const shift = (e as React.MouseEvent | undefined)?.shiftKey;
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastClickedRef.current && lastClickedRef.current !== ordernumber) {
        const a = visibleOrdernumbers.indexOf(lastClickedRef.current);
        const b = visibleOrdernumbers.indexOf(ordernumber);
        if (a !== -1 && b !== -1) {
          const [from, to] = a < b ? [a, b] : [b, a];
          for (let i = from; i <= to; i++) next.add(visibleOrdernumbers[i]);
          lastClickedRef.current = ordernumber;
          return next;
        }
      }
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      lastClickedRef.current = ordernumber;
      return next;
    });
  };

  

  const toggleExpand = (ordernumber: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  };

  // Bulk-acties (print, Aangemaakt, Sikkens-export) mogen UITSLUITEND op rijen werken
  // die zowel geselecteerd ALS op dit moment zichtbaar zijn. Verborgen selecties
  // (door filter/weergave) worden expliciet uitgesloten zodat ze nooit "stiekem"
  // meegeprint worden.
  const selectedRows: OrdersDecoprofRow[] = useMemo(
    () => filteredOrders.filter((o) => selected.has(o.ordernumber) && o.orderid != null),
    [filteredOrders, selected],
  );

  const canPrintPackingSlip = hasPermission("ordersDecoprof", "read");
  // Bulk-acties acteren alleen op zichtbare geselecteerde rijen — knop is dus
  // pas actief als er minstens 1 zichtbare selectie is.
  const hasSelection = visibleSelectedCount >= 1;
  const printDisabled = !canPrintPackingSlip || !hasSelection;
  const printDisabledReason = !canPrintPackingSlip
    ? "Geen toegang tot Orders Decoprof"
    : !hasSelection
      ? "Selecteer minimaal 1 zichtbare order"
      : "";

  const [isPrinting, setIsPrinting] = useState(false);
  const [isMarkingCreated, setIsMarkingCreated] = useState(false);

  // Re-entrancy guard + 5s fingerprint debounce om dubbele print-jobs te voorkomen
  // (double-click race: setIsPrinting is async, disabled rendert pas na 2e click).
  const printLockRef = useRef(false);
  const lastPrintFingerprintRef = useRef<{ key: string; ts: number } | null>(null);
  const PRINT_DEBOUNCE_MS = 5000;

  const removeFromSelection = (ordernumbers: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ordernumbers.forEach((n) => next.delete(n));
      return next;
    });
  };

  const runPrint = async (rows: OrdersDecoprofRow[], preOpenedWindow?: Window | null) => {
    if (rows.length === 0) {
      if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
      return;
    }
    const orderids = rows.map((o) => o.orderid as number);
    const rowById = new Map<number, OrdersDecoprofRow>(
      rows.map((o) => [o.orderid as number, o]),
    );
    const progressToastId = rows.length > 5 ? `batch-print-${Date.now()}` : undefined;
    if (progressToastId) {
      toast.loading(`Pakbonnen 0/${rows.length}...`, { id: progressToastId });
    }

    const result = await printPackingSlipsBatch(orderids, preOpenedWindow, {
      // Per chunk direct de status bijwerken + cache verversen, niet pas op het eind.
      onChunkDone: async (p) => {
        const chunkSucceeded = p.result.succeededOrderids
          .map((id) => rowById.get(id))
          .filter((r): r is OrdersDecoprofRow => !!r);
        const chunkFailed = p.result.failedOrderids
          .map((id) => rowById.get(id))
          .filter((r): r is OrdersDecoprofRow => !!r);

        if (chunkSucceeded.length > 0) {
          try {
            await updateStatus.mutateAsync({
              ordernumbers: chunkSucceeded.map((o) => o.ordernumber),
              status: "Uitgeprint",
            });
            removeFromSelection(chunkSucceeded.map((o) => o.ordernumber));
          } catch (e: any) {
            toast.warning(`PDF gedownload, status-update faalde: ${e?.message ?? e}`);
          }
        }

        setPrintFailed((prev) => {
          const next = new Set(prev);
          chunkSucceeded.forEach((r) => next.delete(r.ordernumber));
          chunkFailed.forEach((r) => next.add(r.ordernumber));
          return next;
        });

        if (progressToastId) {
          toast.loading(`Pakbonnen ${p.processed}/${p.total}...`, { id: progressToastId });
        }
      },
    });

    const succeededRows = rows.filter((o) =>
      result.succeededOrderids.includes(o.orderid as number),
    );
    const failedRows = rows.filter((o) =>
      result.failedOrderids.includes(o.orderid as number),
    );

    if (failedRows.length === 0) {
      toast.success(
        `Pakbon-PDF gedownload — ${succeededRows.length} order${succeededRows.length === 1 ? "" : "s"} → Uitgeprint`,
        progressToastId ? { id: progressToastId } : undefined,
      );
    } else {
      const reasonStr = Object.entries(result.failedReasons)
        .map(([id, r]) => `${id}: ${r}`).join("; ");
      toast.warning(
        `${succeededRows.length} gelukt, ${failedRows.length} mislukt (${failedRows.map((r) => r.ordernumber).join(", ")})${reasonStr ? ` — ${reasonStr}` : ""}`,
        progressToastId ? { id: progressToastId } : undefined,
      );
    }
  };


  const handlePrint = async () => {
    if (selectedRows.length === 0) return;
    const ids = selectedRows.map((o) => o.orderid as number);
    const fingerprint = `bulk:${[...ids].sort((a, b) => a - b).join(",")}`;
    const now = Date.now();
    if (printLockRef.current) {
      toast.info("Print al bezig — even geduld.");
      return;
    }
    const last = lastPrintFingerprintRef.current;
    if (last && last.key === fingerprint && now - last.ts < PRINT_DEBOUNCE_MS) {
      toast.info("Dezelfde pakbon werd zojuist al gedownload — wacht 5 sec.");
      return;
    }
    printLockRef.current = true;
    lastPrintFingerprintRef.current = { key: fingerprint, ts: now };
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "(no-ua)";
    console.log(`[R45 batch-print] click — selected=${selected.size} rows=${selectedRows.length} ids=[${ids.join(",")}] ua="${ua}"`);
    // R45: open SYNCHROON een tabblad binnen de user-gesture op mobiel,
    // anders blokkeert iOS Safari de latere window.open na await.
    let preOpenedWindow: Window | null = null;
    if (isMobileDevice()) {
      try {
        preOpenedWindow = window.open("about:blank", "_blank");
      } catch { preOpenedWindow = null; }
    }
    setIsPrinting(true);
    try {
      await runPrint(selectedRows, preOpenedWindow);
    } catch (e: any) {
      if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
      console.error("[batch-print] error", e);
      const reasons = e?.failedReasons as Record<string, string> | undefined;
      const detail = reasons && Object.keys(reasons).length > 0
        ? ` — ${Object.entries(reasons).map(([id, r]) => `${id}: ${r}`).join("; ")}`
        : "";
      toast.error(`Pakbon afdrukken mislukt: ${e?.message ?? e}${detail}`);
      setPrintFailed((prev) => {
        const next = new Set(prev);
        selectedRows.forEach((r) => next.add(r.ordernumber));
        return next;
      });
    } finally {
      setIsPrinting(false);
      printLockRef.current = false;
    }
  };

  const handleRetryPrint = async (row: OrdersDecoprofRow) => {
    if (row.orderid == null) return;
    setIsPrinting(true);
    try {
      await runPrint([row]);
    } catch (e: any) {
      toast.error(`Retry mislukt: ${e?.message ?? e}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // R50: single-order print vanaf de tegel-knop. Gebruikt dezelfde pipeline
  // als bulk maar met 1 element. iOS: open synchroon een leeg tabblad in de
  // user-gesture (R45 fix) en geef het door aan printPackingSlipsBatch.
  const handleTilePrint = async (row: OrdersDecoprofRow) => {
    if (row.orderid == null) return;
    if (!canPrintPackingSlip) return;
    if (printingOrders.has(row.ordernumber)) return;

    // 5s fingerprint debounce — voorkomt dat snelle dubbele click 2 PDFs triggert
    const fingerprint = `tile:${row.orderid}`;
    const now = Date.now();
    const last = lastPrintFingerprintRef.current;
    if (last && last.key === fingerprint && now - last.ts < PRINT_DEBOUNCE_MS) {
      toast.info(`Pakbon ${row.ordernumber} werd zojuist al gedownload — wacht 5 sec.`);
      return;
    }
    lastPrintFingerprintRef.current = { key: fingerprint, ts: now };

    let preOpenedWindow: Window | null = null;
    if (isMobileDevice()) {
      try { preOpenedWindow = window.open("about:blank", "_blank"); }
      catch { preOpenedWindow = null; }
    }
    setPrintingOrders((prev) => {
      const next = new Set(prev); next.add(row.ordernumber); return next;
    });
    try {
      await runPrint([row], preOpenedWindow);
      setPrintErrorReasons((prev) => {
        if (!(row.ordernumber in prev)) return prev;
        const next = { ...prev }; delete next[row.ordernumber]; return next;
      });
    } catch (e: any) {
      if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
      const reasons = e?.failedReasons as Record<string, string> | undefined;
      const reason = reasons && row.orderid != null ? reasons[String(row.orderid)] : undefined;
      const msg = reason ?? e?.message ?? String(e);
      toast.error(`Pakbon ${row.ordernumber} mislukt: ${msg}`);
      setPrintFailed((prev) => { const next = new Set(prev); next.add(row.ordernumber); return next; });
      setPrintErrorReasons((prev) => ({ ...prev, [row.ordernumber]: msg }));
    } finally {
      setPrintingOrders((prev) => {
        const next = new Set(prev); next.delete(row.ordernumber); return next;
      });
    }
  };

  const handleMarkCreated = async () => {
    if (selectedRows.length === 0) return;
    setIsMarkingCreated(true);
    try {
      const ordernumbers = selectedRows.map((o) => o.ordernumber);
      const res = await updateStatus.mutateAsync({ ordernumbers, status: "Aangemaakt" });
      if (!res?.count || res.count === 0) {
        toast.error("Status niet opgeslagen, je sessie is mogelijk verlopen. Log opnieuw in.");
        return;
      }
      toast.success(`${ordernumbers.length} order${ordernumbers.length === 1 ? "" : "s"} → Aangemaakt`);
      removeFromSelection(ordernumbers);

    } catch (e: any) {
      toast.error(`Status update mislukt: ${e?.message ?? e}`);
    } finally {
      setIsMarkingCreated(false);
    }
  };

  // R77 — knop "Sikkens Center bestellijst" verschijnt zodra ≥1 geselecteerde order
  // de leverancier "Sikkens Center en Simonis" bevat.
  const sikkensSelectedCount = useMemo(() => {
    if (selected.size === 0) return 0;
    return filteredOrders.reduce(
      (n, o) =>
        selected.has(o.ordernumber) &&
        getOrderSuppliers(o.suppliers).includes(SIKKENS_CENTER_SUPPLIER)
          ? n + 1
          : n,
      0,
    );
  }, [selected, filteredOrders]);
  const selectionContainsSikkensCenter = sikkensSelectedCount > 0;

  const [isExportingSikkens, setIsExportingSikkens] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // R77 — splits geselecteerde Sikkens-orders in nieuw vs al-geëxporteerd.
  const sikkensSelectionPartition = useMemo(() => {
    const all: string[] = [];
    const fresh: string[] = [];
    const exported: string[] = [];
    for (const o of filteredOrders) {
      if (!selected.has(o.ordernumber)) continue;
      if (!getOrderSuppliers(o.suppliers).includes(SIKKENS_CENTER_SUPPLIER)) continue;
      all.push(o.ordernumber);
      const isExported = o.orderid != null && sikkensExportMap?.has(o.orderid);
      if (isExported) exported.push(o.ordernumber);
      else fresh.push(o.ordernumber);
    }
    return { all, fresh, exported };
  }, [filteredOrders, selected, sikkensExportMap]);

  const runSikkensExport = async (ordernumbers: string[]) => {
    if (ordernumbers.length === 0) return;
    setIsExportingSikkens(true);
    try {
      const res = await generateSikkensCenterExcel(ordernumbers);
      toast.success(
        `Excel gedownload — ${res.rowCount} regel(s), ${res.orderCount} order(s)`,
      );
    } catch (e: any) {
      toast.error(`Sikkens Center export mislukt: ${e?.message ?? e}`);
    } finally {
      setIsExportingSikkens(false);
    }
  };

  const handleExportSikkens = async () => {
    const { all, exported } = sikkensSelectionPartition;
    if (all.length === 0) return;
    if (exported.length === 0) {
      await runSikkensExport(all);
      return;
    }
    setExportDialogOpen(true);
  };


  return (
    <div className={cn("space-y-4", selected.size > 0 && "pb-44 md:pb-0")}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-start gap-3">
        <Filter3DDropdown
          label="Type order"
          options={ORDER_DECOPROF_TYPE_OPTIONS as readonly string[]}
          values={typeFilter}
          onChange={(v) => setTypeFilter(v as OrderDecoprofType[])}
          pillColor={(v) => TYPE_PILL_COLOR[v] ?? ""}
        />
        <Filter3DDropdown
          label="Status"
          options={ORDER_DECOPROF_STATUSES as readonly string[]}
          values={statusFilter}
          onChange={(v) => setStatusFilter(v as OrderDecoprofStatus[])}
          pillColor={(v) => STATUS_PILL_COLOR[v] ?? ""}
        />
        <Filter3DDropdown
          label="Leverancier"
          options={allSuppliers}
          values={supplierFilter}
          onChange={setSupplierFilter}
          searchable
          searchPlaceholder="Zoek leverancier..."
          emptySearchText="Geen leveranciers gevonden"
        />
        {/* R71: validatie-toggle — alleen orders met 2+ types */}
        <button
          type="button"
          onClick={() => setMultiTypeOnly((v) => !v)}
          className={cn(
            "relative inline-flex items-center gap-2 h-10 px-3 rounded-full text-sm font-medium transition-all duration-150",
            "ring-1 ring-slate-300/60 hover:-translate-y-0.5",
            multiTypeOnly
              ? "bg-gradient-to-b from-pink-500 to-pink-600 text-white ring-pink-300/50 shadow-[0_2px_4px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.3)]"
              : "bg-gradient-to-b from-white to-slate-100 text-slate-700 shadow-[0_2px_4px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.7)]",
          )}
          title="Toon alleen orders met meerdere types (validatie)"
        >
          Multi-type orders
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-full text-xs font-semibold h-5 min-w-[1.25rem] px-1.5",
              multiTypeOnly ? "bg-white/25 text-white" : "bg-slate-700 text-white",
            )}
          >
            {multiTypeCount}
          </span>
        </button>

        {/* R75: validatie-toggle — alleen orders met 2+ unieke leveranciers */}
        <button
          type="button"
          onClick={() => setMultiSupplierOnly((v) => !v)}
          className={cn(
            "relative inline-flex items-center gap-2 h-10 px-3 rounded-full text-sm font-medium transition-all duration-150",
            "ring-1 ring-slate-300/60 hover:-translate-y-0.5",
            multiSupplierOnly
              ? "bg-gradient-to-b from-slate-700 to-slate-800 text-white ring-slate-400/50 shadow-[0_2px_4px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.2)]"
              : "bg-gradient-to-b from-white to-slate-100 text-slate-700 shadow-[0_2px_4px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.7)]",
          )}
          title="Toon alleen orders met meerdere leveranciers (validatie)"
        >
          Multi-leverancier orders
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-full text-xs font-semibold h-5 min-w-[1.25rem] px-1.5",
              multiSupplierOnly ? "bg-white/25 text-white" : "bg-slate-700 text-white",
            )}
          >
            {multiSupplierCount}
          </span>
        </button>

        {/* FASE 2 punt 1 — toggle 'Toon ook afhalen-orders' (default OFF) */}
        <button
          type="button"
          onClick={() => setShowPickup((v) => !v)}
          className={cn(
            "relative inline-flex items-center gap-2 h-10 px-3 rounded-full text-sm font-medium transition-all duration-150",
            "ring-1 ring-slate-300/60 hover:-translate-y-0.5",
            showPickup
              ? "bg-gradient-to-b from-amber-500 to-amber-600 text-white ring-amber-300/50 shadow-[0_2px_4px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.3)]"
              : "bg-gradient-to-b from-white to-slate-100 text-slate-700 shadow-[0_2px_4px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.7)]",
          )}
          title="Toon ook orders met afhalen in winkel (standaard verborgen)"
        >
          Toon ook afhalen-orders
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-full text-xs font-semibold h-5 min-w-[1.25rem] px-1.5",
              showPickup ? "bg-white/25 text-white" : "bg-slate-700 text-white",
            )}
          >
            {pickupHiddenCount}
          </span>
        </button>






        <div className="ml-auto flex gap-2 flex-wrap">
          {/* R77: Excel-export Sikkens Center bestellijst (verschijnt bij selectie met ≥1 Sikkens Center order) */}
          <TooltipProvider>
            {selectionContainsSikkensCenter && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button3D
                      tone="primary"
                      onClick={handleExportSikkens}
                      disabled={isExportingSikkens}
                    className="sm:min-w-[18rem] sm:[&>span:last-child]:justify-center"
                  >
                    {isExportingSikkens ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline pointer-events-none text-center">
                      <span className="tabular-nums">{sikkensSelectedCount}</span>
                      {sikkensSelectedCount === 1
                        ? " Sikkens Center order exporteren"
                        : " Sikkens Center orders exporteren"}
                    </span>
                    <span className="sm:hidden pointer-events-none">
                      XLS (<span className="tabular-nums">{sikkensSelectedCount}</span>)
                    </span>
                    </Button3D>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Download Excel met alleen de Trimetal/SPS-regels van geselecteerde orders
                </TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button3D
                    tone="violet"
                    onClick={handleMarkCreated}
                    disabled={printDisabled || updateStatus.isPending || isMarkingCreated || isPrinting}
                    className="sm:min-w-[14rem] sm:[&>span:last-child]:justify-center"
                  >
                    {isMarkingCreated ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileCheck2 className="h-4 w-4" />
                    )}
                    {/* R43: pointer-events-none op alle inner spans → klik landt altijd op <button>, niet op inner text. */}
                    <span className="hidden sm:inline pointer-events-none text-center">
                      {visibleSelectedCount === 0 ? (
                        "Aangemaakt"
                      ) : (
                        <>
                          <span className="tabular-nums">{visibleSelectedCount}</span>
                          {visibleSelectedCount === 1 ? " order op Aangemaakt" : " orders op Aangemaakt"}
                        </>
                      )}
                    </span>
                    <span className="sm:hidden pointer-events-none tabular-nums">
                      {visibleSelectedCount === 0 ? "" : visibleSelectedCount}
                    </span>
                  </Button3D>
                </span>
              </TooltipTrigger>
              {printDisabledReason && <TooltipContent>{printDisabledReason}</TooltipContent>}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button3D
                    tone="primary"
                    onClick={handlePrint}
                    disabled={printDisabled || updateStatus.isPending || isPrinting || isMarkingCreated}
                    className="sm:min-w-[13rem] sm:[&>span:last-child]:justify-center"
                  >
                    {isPrinting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline pointer-events-none text-center">
                      {visibleSelectedCount === 0 ? (
                        "Pakbon afdrukken"
                      ) : (
                        <>
                          <span className="tabular-nums">{visibleSelectedCount}</span>
                          {visibleSelectedCount === 1 ? " order afdrukken" : " orders afdrukken"}
                        </>
                      )}
                    </span>
                    <span className="sm:hidden pointer-events-none tabular-nums">
                      {visibleSelectedCount === 0 ? "" : visibleSelectedCount}
                    </span>
                  </Button3D>
                </span>
              </TooltipTrigger>
              {printDisabledReason && <TooltipContent>{printDisabledReason}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {!isLoading && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""} gevonden
          </p>
          {selected.size > 0 && (
            <p className="text-xs text-muted-foreground">
              {selected.size} geselecteerd
              {hiddenSelectedCount > 0 && `, ${hiddenSelectedCount} verborgen door filter`}
            </p>
          )}
          {(() => {
            const activeCount =
              (typeFilter.length > 0 ? 1 : 0) +
              (statusFilter.length > 0 ? 1 : 0) +
              (supplierFilter.length > 0 ? 1 : 0) +
              (multiTypeOnly ? 1 : 0) +
              (multiSupplierOnly ? 1 : 0) +
              (showPickup ? 1 : 0) +
              (afgerondSearch.trim() ? 1 : 0);
            if (activeCount === 0) return null;
            return (
              <button
                type="button"
                onClick={() => {
                  setTypeFilter([]);
                  setStatusFilter([]);
                  setSupplierFilter([]);
                  setMultiTypeOnly(false);
                  setMultiSupplierOnly(false);
                  setShowPickup(false);
                  setAfgerondSearch("");
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary ring-1 ring-primary/30 px-3 py-1 text-xs font-semibold hover:bg-primary/20 transition-colors"
                title="Wis alle actieve filters en zoektermen"
              >
                Filters wissen
                <span className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground h-4 min-w-4 px-1 text-[10px]">
                  {activeCount}
                </span>
              </button>
            );
          })()}
        </div>
      )}


      {/* Zoekbalk: altijd zichtbaar — werkt in zowel reguliere als Afgerond-weergave */}
      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={afgerondSearch}
          onChange={(e) => setAfgerondSearch(e.target.value)}
          placeholder={
            isAfgerondActief
              ? "Zoek in afgeronde orders op ordernummer, klantnaam of klantnummer"
              : "Zoek op ordernummer, klantnaam of klantnummer"
          }
          className="pl-9"
        />
      </div>

      {/* Header-rij met master-checkbox; symmetrisch gecentreerd t.o.v. data-cellen */}
      <div className="hidden lg:grid grid-cols-[36px_96px_108px_minmax(120px,1.2fr)_112px_88px_minmax(140px,1fr)_116px_220px] gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground items-center">
        <div className="flex justify-center">
          <Checkbox
            checked={masterState}
            onCheckedChange={toggleMaster}
            aria-label="Selecteer alle zichtbare orders"
            disabled={visibleOrdernumbers.length === 0}
          />
        </div>
        <div className="text-center">ORDERNUMMER</div>
        <div className="text-center">AANMAAKDATUM</div>
        <div className="text-center">KLANTNAAM</div>
        <div className="text-center">TYPE</div>
        <div className="text-center">VERZENDDATUM</div>
        <div className="text-center">LEVERANCIER</div>
        <div className="text-center">STATUS</div>
        <div className="text-center">ACTIES</div>
      </div>

      <div className="space-y-2">
        {isLoading ? (
          <Card3D className="px-4 py-8 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
            Laden...
          </Card3D>
        ) : error ? (
          <Card3D className="px-4 py-8 text-center text-destructive">
            Fout bij laden van orders
          </Card3D>
        ) : filteredOrders.length === 0 ? (
          <Card3D className="px-4 py-8 text-center text-muted-foreground">
            {isAfgerondActief
              ? (afgerondSearch.trim()
                  ? "Geen afgeronde orders gevonden voor deze zoekopdracht"
                  : "Geen afgeronde orders gevonden")
              : multiSupplierOnly
                ? "Geen multi-leverancier orders gevonden in de huidige weergave"
                : multiTypeOnly
                  ? "Geen multi-type orders gevonden in de huidige weergave"
                  : "Geen Decoprof-orders gevonden"}
          </Card3D>
        ) : (
          (() => {
            const renderOrder = (order: OrdersDecoprofRow) => {
            const isExpanded = expanded.has(order.ordernumber);
            const isFailed = printFailed.has(order.ordernumber);
            const isFlashing = flashing.has(order.ordernumber);
            const customerDisplayName = extractCustomerDisplayName(order);
            const visibleShippingDate = getVisibleShippingDate(order);
            const hasOrderNotes = order.orderid != null && orderIdsWithNotes?.has(order.orderid);
            const isAfwijkendeVerzenddatum = order.status === "Afwijkende verzenddatum";
            const isAfgerond = order.status === "Afgerond";
            const isPickupOrder = isPickupShipment(order.shipment_title);
            const hasFutureShippingDate = isFutureShippingDate(order.shippingdate);
            // Klantnaam: helder afgeleid via getKlantnaam(order) zodat desktop én mobile altijd
            // exact dezelfde naam tonen (voornaam/achternaam → billing_address → "Onbekende klant").
            const customerNameInHeader = getKlantnaam(order);
            const orderTypes = getOrderTypes(order.suppliers);
            const orderSuppliers = getOrderSuppliers(order.suppliers);
            // R77: Sikkens Center badge → groen accent + download-icoon + tooltip met export-datum.
            const sikkensExportedAt = order.orderid != null
              ? sikkensExportMap?.get(order.orderid)
              : undefined;
            const supplierMeta: Record<string, BadgeItemMeta> | undefined = sikkensExportedAt
              ? {
                  [SIKKENS_CENTER_SUPPLIER]: {
                    toneClass: "bg-emerald-50 text-emerald-800 ring-emerald-200",
                    icon: <Download className="h-3 w-3" />,
                    tooltip: `Geëxporteerd op ${formatExportedAt(sikkensExportedAt)}`,
                  },
                }
              : undefined;
            return (
              <Card3D
                key={order.ordernumber}
                className={cn(
                  "transition-transform duration-150 hover:-translate-y-0.5 cursor-pointer overflow-hidden",
                  isAfgerond && "opacity-60 bg-slate-100/60",
                  isFailed && "border-2 border-red-500/60",
                  isFlashing && "animate-[flashIn_1.5s_ease-out] ring-2 ring-primary/40",
                  isPickupOrder && "border-l-4 border-l-sky-500 bg-sky-50/40",
                  hasFutureShippingDate && "md:ring-1 md:ring-violet-300 md:bg-violet-50/35 max-md:border-2 max-md:border-red-500/50 max-md:bg-red-50/20",
                  // FASE-bijsturing punt 4 — subtiel amber accent voor afwijkende verzenddatum
                  isAfwijkendeVerzenddatum &&
                    "border-l-4 border-l-amber-500 bg-amber-50/40",
                )}
                onClick={(e) => {
                  toggleExpand(order.ordernumber);
                  // Houd de geklikte tegel zichtbaar — voorkom dat hij uit beeld duikt
                  const el = (e.currentTarget as HTMLElement);
                  requestAnimationFrame(() => {
                    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                  });
                }}
                title={isFailed ? "Niet afgedrukt — klik op de retry-knop om opnieuw te proberen" : undefined}
              >
                <PrefetchSentinel orderid={order.orderid} />
                {/* R75: items-start + vaste min-h zorgt dat rijen met 1 of meerdere badges dezelfde hoogte hebben en cellen vanaf bovenkant uitlijnen. */}
                <div className="grid grid-cols-[44px_minmax(0,1fr)] sm:grid-cols-[44px_minmax(0,1fr)_auto] lg:grid-cols-[36px_96px_108px_minmax(120px,1.2fr)_112px_88px_minmax(140px,1fr)_116px_220px] items-start lg:min-h-[64px] gap-2 lg:gap-2 px-3 lg:px-3 py-2 lg:py-2">
                  {/* MOBILE: bulk-select checkbox linksboven in de tegel, los van card-click */}
                  <div className="lg:hidden flex items-start justify-center pt-0.5">
                    <div
                      className="flex min-h-11 min-w-11 items-center justify-center p-2 -m-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRow(order.ordernumber, e as any);
                      }}
                    >
                      <Checkbox
                        checked={selected.has(order.ordernumber)}
                        onClick={(e) => e.stopPropagation()}
                        onCheckedChange={() => toggleRow(order.ordernumber)}
                        aria-label={`Selecteer order ${order.ordernumber}`}
                        className="h-5 w-5"
                      />
                    </div>
                  </div>

                  {/* DESKTOP: bulk-select checkbox in de tabelkolom */}
                  <div
                    className="hidden lg:flex items-center justify-center min-h-11 min-w-11 lg:min-h-0 lg:min-w-0 -m-1 lg:m-0 p-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRow(order.ordernumber, e as any);
                    }}
                  >
                    <Checkbox
                      checked={selected.has(order.ordernumber)}
                      onClick={(e) => e.stopPropagation()}
                      onCheckedChange={() => toggleRow(order.ordernumber)}
                      aria-label={`Selecteer order ${order.ordernumber}`}
                      className="h-4 w-4"
                    />
                  </div>


                  {/* DESKTOP: Ordernummer (eigen kolom, gecentreerd) */}
                  <div className="hidden lg:flex flex-col items-center justify-center text-center min-w-0 font-medium">
                    <div className="flex items-center gap-2 flex-wrap justify-center">
                      <span className="text-lg font-semibold leading-none text-foreground">{order.ordernumber}</span>
                      {hasOrderNotes && (
                        <span
                          title="Deze order bevat interne notities"
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 ring-1 ring-amber-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                        >
                          <StickyNote className="h-3 w-3" />
                          notitie
                        </span>
                      )}
                    </div>
                  </div>

                  {/* DESKTOP: Aanmaakdatum (datum + tijd binnenkomst, eigen kolom) */}
                  <div className="hidden lg:flex items-center justify-center text-center text-xs text-muted-foreground tabular-nums">
                    {order.createdAt ? formatOrderDateTime(order.createdAt) : ""}
                  </div>

                  {/* DESKTOP: Klantnaam (eigen kolom, klikbaar voor historie) */}
                  <div className="hidden lg:flex items-center justify-center text-center min-w-0 px-1" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const hasIdentifier = Boolean(order.customer_number) || Boolean(order.emailadres_klant);
                      const baseClass = "text-sm font-semibold text-foreground block text-center w-full break-words whitespace-normal line-clamp-2 leading-tight";

                      if (!hasIdentifier) {
                        return (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={cn(baseClass, "opacity-60 cursor-not-allowed")} title={customerNameInHeader}>
                                  {customerNameInHeader}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>Geen klantnummer beschikbaar</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      }
                      return (
                        <button
                          type="button"
                          className={cn(baseClass, "cursor-pointer hover:underline")}
                          title={customerNameInHeader}
                          onClick={() => setHistoryTarget({
                            name: customerNameInHeader,
                            number: order.customer_number ?? null,
                            email: order.emailadres_klant ?? null,
                          })}
                        >
                          {customerNameInHeader}
                        </button>
                      );
                    })()}
                  </div>


                  {/* DESKTOP: Type kolom */}
                  <div className="hidden lg:flex items-center justify-center">
                    <BadgeRow items={orderTypes} variant="type" />
                  </div>

                  {/* DESKTOP: Verzenddatum kolom */}
                  <div className="hidden lg:flex items-center justify-center text-center text-sm text-muted-foreground tabular-nums">
                    {visibleShippingDate ? (
                      <span
                        className={cn(
                          visibleShippingDate.isFallback && "italic text-muted-foreground/70",
                        )}
                        title={visibleShippingDate.title}
                      >
                        {visibleShippingDate.label}
                      </span>
                    ) : ""}
                  </div>

                  {/* DESKTOP: Leverancier kolom */}
                  <div className="hidden lg:flex items-center justify-center min-w-0 px-1">
                    <BadgeRow items={orderSuppliers} variant="supplier" itemMeta={supplierMeta} />
                  </div>

                  {/* DESKTOP: Status kolom */}
                  <div
                    className="hidden lg:flex items-center justify-center min-w-0 px-1"
                    onClick={(e) => e.stopPropagation()}
                  >

                    <OrderStatusDropdown
                      status={order.status}
                      interactive={hasPermission("ordersDecoprof", "write")}
                      isPending={
                        updateStatus.isPending &&
                        updateStatus.variables?.ordernumber === order.ordernumber
                      }
                      onChange={(next) => {
                        updateStatus.mutate(
                          { ordernumber: order.ordernumber, status: next },
                          {
                            onSuccess: () => toast.success(`${order.ordernumber} → ${next}`),
                            onError: (e: any) => toast.error(`Status update mislukt: ${e?.message ?? e}`),
                          },
                        );
                      }}
                    />
                  </div>

                  {/* MOBILE: gecombineerde card-content (één rendering, geen duplicatie met desktop) */}
                  <div className="lg:hidden min-w-0 font-medium">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg font-semibold leading-none text-foreground">{order.ordernumber}</span>
                      {hasOrderNotes && (
                        <span
                          title="Deze order bevat interne notities"
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 ring-1 ring-amber-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                        >
                          <StickyNote className="h-3 w-3" />
                          notitie
                        </span>
                      )}
                    </div>
                    {(order.createdAt || visibleShippingDate) && (
                      <div className="text-xs font-normal text-muted-foreground mt-0.5 flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                        {order.createdAt && (
                          <span className="tabular-nums">{formatOrderDateTime(order.createdAt)}</span>
                        )}
                        {visibleShippingDate ? (
                          <>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
                            <span
                              className={cn(
                                "tabular-nums",
                                visibleShippingDate.isFallback && "italic text-muted-foreground/70",
                                hasFutureShippingDate && "font-semibold text-red-600",
                              )}
                              title={visibleShippingDate.title}
                            >
                              {visibleShippingDate.label}
                            </span>
                          </>
                        ) : order.createdAt ? (
                          <span className="text-muted-foreground/60">·</span>
                        ) : null}
                      </div>
                    )}

                    {(() => {
                      const hasIdentifier = Boolean(order.customer_number) || Boolean(order.emailadres_klant);
                      const baseClass = "text-sm font-semibold text-foreground break-words whitespace-normal text-left";
                      // Mobiel: de klantnaam zelf is GEEN overlay-opener meer (een
                      // full-width knop ving rij-tikken op en opende per ongeluk de
                      // historie-sheet bij inklappen). Alleen de expliciete knop opent.
                      return (
                        <div className="mt-1 flex items-start gap-2">
                          <span className={cn(baseClass, !hasIdentifier && "opacity-70")} title={hasIdentifier ? undefined : "Geen klantnummer beschikbaar"}>
                            {customerNameInHeader}
                          </span>
                          {hasIdentifier && (
                            <button
                              type="button"
                              aria-label={`Communicatie-historie van ${customerNameInHeader}`}
                              className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground"
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                setHistoryTarget({
                                  name: customerNameInHeader,
                                  number: order.customer_number ?? null,
                                  email: order.emailadres_klant ?? null,
                                });
                              }}
                            >
                              <History className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      );
                    })()}


                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <BadgeRow items={orderTypes} variant="type" />
                      <BadgeRow items={orderSuppliers} variant="supplier" itemMeta={supplierMeta} />
                    </div>
                  </div>

                  <div className="col-span-2 sm:col-span-1 mt-2 sm:mt-0 flex items-center justify-end gap-2 flex-wrap sm:flex-nowrap min-w-0" onClick={(e) => e.stopPropagation()}>
                    <div className="lg:hidden min-h-11 flex items-center">
                      <OrderStatusDropdown
                        status={order.status}
                        interactive={hasPermission("ordersDecoprof", "write")}
                        isPending={
                          updateStatus.isPending &&
                          updateStatus.variables?.ordernumber === order.ordernumber
                        }
                        onChange={(next) => {
                          updateStatus.mutate(
                            { ordernumber: order.ordernumber, status: next },
                            {
                              onSuccess: () => toast.success(`${order.ordernumber} → ${next}`),
                              onError: (e: any) => toast.error(`Status update mislukt: ${e?.message ?? e}`),
                            },
                          );
                        }}
                      />
                    </div>

                    <KlantInlichtenButton
                      orderid={order.orderid ?? null}
                      voNumber={order.ordernumber ?? null}
                      customerNameHint={[order.voornaam_klant, order.achternaam_klant].filter(Boolean).join(' ').trim() || null}
                      variant="full"
                      className="max-w-full lg:max-w-[160px]"
                    />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Tile3DIconButton
                            aria-label={`Pakbon afdrukken voor ${order.ordernumber}`}
                            error={isFailed}
                            disabled={!canPrintPackingSlip || printingOrders.has(order.ordernumber) || order.orderid == null}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTilePrint(order);
                            }}
                          >
                            {printingOrders.has(order.ordernumber) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Printer className="h-[18px] w-[18px]" />
                            )}
                          </Tile3DIconButton>
                        </TooltipTrigger>
                        {(isFailed || !canPrintPackingSlip) && (
                          <TooltipContent>
                            {!canPrintPackingSlip
                              ? "Geen toegang tot Orders Decoprof"
                              : (printErrorReasons[order.ordernumber] ?? "Niet afgedrukt — klik om opnieuw te proberen")}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    {/* R75: chevron-dropdown verwijderd — had geen klik-functie (rij-klik toggelt expand). */}
                  </div>
                </div>
                {/* Expand zonder grid-rows-fr trick: interpolatie van 0fr→1fr wordt niet
                    door alle mobiele browsers ondersteund, waardoor de inhoud op mobiel
                    op hoogte 0 bleef staan (alleen een grijze balk zichtbaar). */}
                <div className="relative z-10" style={{ overflowAnchor: "none" }}>
                  <div>
                    {isExpanded && order.orderid != null && (
                      <div className="px-4 pb-4 pt-1 space-y-3" onClick={(e) => e.stopPropagation()}>
                        {/* FASE 2 punt 4 — uitgebreid klantblok in openklap */}
                        {(order.billing_address || order.telefoonnummer_klant || order.emailadres_klant) && (
                          <div className="rounded-md border bg-background/60 p-3 text-xs space-y-1">
                            <div className="font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">Klantgegevens</div>
                            {customerDisplayName && (
                              <div className="font-medium text-sm">
                                {customerDisplayName}
                              </div>
                            )}
                            {order.billing_address && (order.billing_address.street || order.billing_address.zipcode || order.billing_address.city) && (
                              <div className="text-muted-foreground">
                                {[order.billing_address.street, order.billing_address.number].filter(Boolean).join(" ")}
                                {(order.billing_address.zipcode || order.billing_address.city) && (
                                  <>{", "}{[order.billing_address.zipcode, order.billing_address.city].filter(Boolean).join(" ")}</>
                                )}
                              </div>
                            )}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
                              {order.emailadres_klant && (
                                <a href={`mailto:${order.emailadres_klant}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                                  <Mail className="h-3 w-3" />{order.emailadres_klant}
                                </a>
                              )}
                              {order.telefoonnummer_klant && (
                                <a href={`tel:${order.telefoonnummer_klant.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                                  <Phone className="h-3 w-3" />{order.telefoonnummer_klant}
                                </a>
                              )}
                              {order.customer_number && (
                                <span className="text-muted-foreground">klant #{order.customer_number}</span>
                              )}
                            </div>
                          </div>
                        )}
                        {order.opmerking_klant && order.opmerking_klant.trim() && (
                          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs space-y-1">
                            <div className="font-semibold uppercase tracking-wide text-[10px] text-amber-700">
                              Opmerking van klant (Lightspeed)
                            </div>
                            <div className="text-sm text-amber-900 whitespace-pre-wrap italic">
                              "{order.opmerking_klant.trim()}"
                            </div>
                          </div>
                        )}
                        <OrderLinesExpansion orderid={order.orderid} ordernumber={order.ordernumber} />
                      </div>
                    )}
                  </div>
                </div>
              </Card3D>
            );
            };
            return <VirtualOrdersList orders={filteredOrders} renderOrder={renderOrder} flashing={flashing} />;
          })()
        )}
      </div>

      <ScrollHelpers />

      <ExportConfirmDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        total={sikkensSelectionPartition.all.length}
        alreadyExported={sikkensSelectionPartition.exported.length}
        onlyNewCount={sikkensSelectionPartition.fresh.length}
        onExportNew={() => {
          setExportDialogOpen(false);
          void runSikkensExport(sikkensSelectionPartition.fresh);
        }}
        onExportAll={() => {
          setExportDialogOpen(false);
          void runSikkensExport(sikkensSelectionPartition.all);
        }}
      />

      <KlantHistorySheet
        open={historyTarget !== null}
        onOpenChange={(open) => { if (!open) setHistoryTarget(null); }}
        customerName={historyTarget?.name ?? ""}
        customerNumber={historyTarget?.number ?? null}
        customerEmail={historyTarget?.email ?? null}
      />

      {/* Mobile sticky bulk action bar — alleen op kleine schermen, alleen bij selectie */}
      {selected.size > 0 && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur shadow-[0_-6px_18px_rgba(0,0,0,0.10)]">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col text-xs leading-tight">
              <span className="font-semibold tabular-nums">{selected.size} geselecteerd</span>
              {hiddenSelectedCount > 0 && (
                <span className="text-muted-foreground">{hiddenSelectedCount} verborgen</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline px-2 py-1 min-h-11"
              aria-label="Selectie wissen"
            >
              Wissen
            </button>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {selectionContainsSikkensCenter && (
              <Button3D
                tone="primary"
                onClick={handleExportSikkens}
                disabled={isExportingSikkens}
                className="col-span-2 min-h-11 justify-center"
              >
                {isExportingSikkens ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4" />
                )}
                <span className="pointer-events-none">
                  XLS Sikkens (<span className="tabular-nums">{sikkensSelectedCount}</span>)
                </span>
              </Button3D>
            )}
            <Button3D
              tone="violet"
              onClick={handleMarkCreated}
              disabled={printDisabled || updateStatus.isPending || isMarkingCreated || isPrinting}
              className="min-h-11 justify-center"
            >
              {isMarkingCreated ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileCheck2 className="h-4 w-4" />
              )}
              <span className="pointer-events-none">Aangemaakt</span>
            </Button3D>
            <Button3D
              tone="primary"
              onClick={handlePrint}
              disabled={printDisabled || updateStatus.isPending || isPrinting || isMarkingCreated}
              className="min-h-11 justify-center"
            >
              {isPrinting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Printer className="h-4 w-4" />
              )}
              <span className="pointer-events-none">Afdrukken</span>
            </Button3D>
          </div>
        </div>
      )}
    </div>


  );
};

/**
 * R52: window-virtualizer rondom de orderlijst.
 * - Gebruikt `useWindowVirtualizer` zodat de bestaande page-scroll (en de
 *   ScrollHelpers FAB die window.scrollTo gebruikt) intact blijft.
 * - `measureElement` voor variabele rij-hoogte (uitgevouwen tegels).
 * - Behoudt selectie/expand-state: state leeft in de parent en is gekoppeld
 *   aan ordernumber, dus virtualisatie raakt het niet.
 */
interface VirtualOrdersListProps {
  orders: OrdersDecoprofRow[];
  renderOrder: (order: OrdersDecoprofRow) => React.ReactNode;
  flashing: Set<string>;
}

const VirtualOrdersList = ({ orders, renderOrder }: VirtualOrdersListProps) => {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [parentOffset, setParentOffset] = useState(0);

  useEffect(() => {
    const measure = () => {
      const el = parentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setParentOffset(rect.top + window.scrollY);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: orders.length,
    estimateSize: () => 104, // R56: 72 was structureel te laag; realistischer estimate voorkomt groeiend "onderaan"
    overscan: 5,
    scrollMargin: parentOffset,
    getItemKey: (index) => orders[index].ordernumber,
  });

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <div ref={parentRef} className="relative" style={{ height: totalSize }}>
      {items.map((vi) => {
        const order = orders[vi.index];
        return (
          <div
            key={vi.key}
            data-index={vi.index}
            ref={virtualizer.measureElement}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              transform: `translateY(${vi.start - virtualizer.options.scrollMargin}px)`,
              paddingBottom: 8, // mimics space-y-2
            }}
          >
            {renderOrder(order)}
          </div>
        );
      })}
    </div>
  );
};

export default OrdersDecoprofTab;
